// Connection Suggestions for AgendaPage
// Suggests connections to schedule conversations with

import React, { useEffect, useState } from 'react';
import { Users, Calendar, ArrowRight } from 'lucide-react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { MotionCard } from '../ui/motion-card';

interface ConnectionSuggestionsProps {
  userId: string;
  onScheduleWith: (connectionId: string, connectionName: string) => void;
}

interface Suggestion {
  id: string;
  name: string;
  photoURL?: string;
  lastInteraction?: Date;
  phase: string;
}

const ConnectionSuggestions: React.FC<ConnectionSuggestionsProps> = ({ 
  userId, 
  onScheduleWith 
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuggestions();
  }, [userId]);

  const loadSuggestions = async () => {
    try {
      // Get connections that haven't had a recent conversation
      const connectionsQ = query(
        collection(db, 'connections'),
        where('participants', 'array-contains', userId),
        orderBy('lastInteractionAt', 'desc'),
        limit(5)
      );
      
      const snap = await getDocs(connectionsQ);
      const results: Suggestion[] = [];
      
      for (const doc of snap.docs) {
        const data = doc.data();
        const otherUserId = (data.participants as string[]).find(id => id !== userId);
        
        if (otherUserId) {
          // Get other user's profile
          const userDoc = await getDocs(
            query(collection(db, 'users'), where('uid', '==', otherUserId), limit(1))
          );
          
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            results.push({
              id: doc.id,
              name: userData.displayName || 'Onbekend',
              photoURL: userData.photoURL,
              lastInteraction: data.lastInteractionAt?.toDate(),
              phase: data.phase || 'chat'
            });
          }
        }
      }
      
      setSuggestions(results);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="space-y-2">
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <MotionCard className="p-4">
      <div className="flex items-center space-x-2 mb-3">
        <Users size={18} className="text-blue-500" />
        <h4 className="font-semibold text-gray-800">Aanbevolen gesprekken</h4>
      </div>

      <div className="space-y-2">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              {suggestion.photoURL ? (
                <img
                  src={suggestion.photoURL}
                  alt={suggestion.name}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    const initials = (suggestion.name || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=80`;
                  }}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                  {suggestion.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-medium text-gray-800 text-sm">{suggestion.name}</p>
                {suggestion.lastInteraction && (
                  <p className="text-xs text-gray-500">
                    Laatste gesprek: {formatDate(suggestion.lastInteraction)}
                  </p>
                )}
              </div>
            </div>
            
            <button
              onClick={() => onScheduleWith(suggestion.id, suggestion.name)}
              className="p-2 hover:bg-blue-50 rounded-full transition-colors group"
              title="Plan gesprek"
            >
              <Calendar size={18} className="text-blue-500 group-hover:text-blue-600" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={() => {/* Navigate to connections */}}
        className="w-full mt-3 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center space-x-1"
      >
        <span>Alle connecties</span>
        <ArrowRight size={14} />
      </button>
    </MotionCard>
  );
};

function formatDate(date: Date): string {
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Vandaag';
  if (diffDays === 1) return 'Gisteren';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`;
  return `${Math.floor(diffDays / 30)} maanden geleden`;
}

export default ConnectionSuggestions;
